import React, { useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface GuidelineViolation {
  severity: 'error' | 'warning' | 'info';
  rule: string;
  message: string;
  line_item_id?: string;
  line_item_description?: string;
}

interface BillGuidelineReport {
  bill_id: string;
  guideline_name: string;
  compliance_score: number;
  violations: GuidelineViolation[];
  error_count: number;
  warning_count: number;
}

interface Props {
  billId: string;
}

const RULE_LABELS: Record<string, string> = {
  rate_cap: 'Rate Cap',
  max_hours: 'Max Hours',
  min_description: 'Description Length',
  block_billing: 'Block Billing',
  prohibited_code: 'Prohibited Code',
  prohibited_keyword: 'Prohibited Keyword',
  require_utbms: 'UTBMS Required',
  fee_split_info: 'Fee Split Allocation',
  fee_split_exceeded: 'Fee Split Exceeded',
  co_counsel_rate: 'Co-Counsel Rate Cap',
};

const GuidelineViolationPanel: React.FC<Props> = ({ billId }) => {
  const { keycloak } = useKeycloak();
  const [report, setReport] = useState<BillGuidelineReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/billing/guidelines/validate-bill/${billId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${keycloak?.token || ''}`,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || 'Compliance check failed.');
      }
      const body = await res.json();
      setReport(body.data);
    } catch (err: any) {
      setError(err.message || 'Failed to run compliance check.');
    } finally {
      setLoading(false);
    }
  }, [billId, keycloak?.token]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 70) return 'bg-yellow-100 dark:bg-yellow-900/30';
    if (score >= 50) return 'bg-orange-100 dark:bg-orange-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  if (!report) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Guideline Compliance</h3>
          </div>
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Check this invoice against active billing guidelines and outside counsel rules.
        </p>
        <button
          onClick={runCheck}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <ShieldExclamationIcon className="h-4 w-4" />
              Check Compliance
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      {/* Header with score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Guideline Compliance</h3>
        </div>
        <button
          onClick={runCheck}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Re-check
        </button>
      </div>

      {/* Score */}
      <div className={`flex items-center gap-4 p-4 rounded-lg ${getScoreBg(report.compliance_score)}`}>
        <div className={`text-3xl font-bold ${getScoreColor(report.compliance_score)}`}>
          {report.compliance_score}
        </div>
        <div>
          <div className={`text-sm font-medium ${getScoreColor(report.compliance_score)}`}>
            Compliance Score
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {report.error_count} error{report.error_count !== 1 ? 's' : ''}, {report.warning_count} warning{report.warning_count !== 1 ? 's' : ''}
          </div>
        </div>
        {report.compliance_score === 100 && (
          <CheckCircleIcon className="h-8 w-8 text-green-500 ml-auto" />
        )}
      </div>

      {/* Violations */}
      {report.violations.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Violations ({report.violations.length})
          </h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {report.violations.map((v, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  v.severity === 'error'
                    ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                    : v.severity === 'info'
                    ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800'
                }`}
              >
                {v.severity === 'error' ? (
                  <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                ) : v.severity === 'info' ? (
                  <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                      v.severity === 'error'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : v.severity === 'info'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}>
                      {RULE_LABELS[v.rule] || v.rule}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{v.message}</p>
                  {v.line_item_description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      Line item: {v.line_item_description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-700 dark:text-green-300">
            No violations found. This invoice is fully compliant.
          </span>
        </div>
      )}
    </div>
  );
};

export default GuidelineViolationPanel;
